import { useMemo, useRef, useState } from 'react'
import { TopBar } from '../components/TopBar'
import { Avatar, roleLabels } from '../components/Avatar'
import { useApp } from '../store/AppContext'
import { localISODate } from '../lib/date'
import { DAY_LABELS, WEEK_DAYS, dayOfWeekFor, shiftDateLabel } from '../lib/cover'
import { profilePhotoFor, removeProfilePhoto, resizeProfilePhoto, saveProfilePhoto } from '../lib/profilePhotos'
import {
  CREDENTIAL_TYPES,
  credentialStatus,
  credentialsForWorker,
  saveCredential,
  type CredentialType,
} from '../lib/credentials'
import type { DayOfWeek } from '../types'

const shortLabel: Record<DayOfWeek, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
}

function nextSevenDays(): string[] {
  const out: string[] = []
  const today = new Date()
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(today)
    d.setDate(today.getDate() + i)
    out.push(localISODate(d))
  }
  return out
}

function CameraIcon() {
  return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M4 8.5h3l1.2-2h7.6l1.2 2h3v10H4z"/><circle cx="12" cy="13.5" r="3.2"/></svg>
}

export function Profile() {
  const { currentUser, getAvailability, updateAvailability } = useApp()
  const canSetAvailability = currentUser.role === 'support_worker'
  const saved = getAvailability(currentUser.id)
  const photoInputRef = useRef<HTMLInputElement>(null)

  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>(saved.availableDays)
  const [showOnCalendar, setShowOnCalendar] = useState(saved.showOnCalendar)
  const [justSaved, setJustSaved] = useState(false)
  const [photoVersion, setPhotoVersion] = useState(0)
  const [photoError, setPhotoError] = useState('')
  const [photoBusy, setPhotoBusy] = useState(false)
  const upcoming = useMemo(nextSevenDays, [])
  const chosen = WEEK_DAYS.filter((d) => selectedDays.includes(d))
  const dirty = showOnCalendar !== saved.showOnCalendar || chosen.length !== saved.availableDays.length || chosen.some((d) => !saved.availableDays.includes(d))
  const hasPhoto = useMemo(() => Boolean(profilePhotoFor(currentUser.id)), [currentUser.id, photoVersion])

  const toggleDay = (day: DayOfWeek) => {
    setSelectedDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]))
  }

  const save = () => {
    updateAvailability({ userId: currentUser.id, availableDays: chosen, showOnCalendar })
    setJustSaved(true)
    setTimeout(() => setJustSaved(false), 1500)
  }

  const choosePhoto = async (file?: File) => {
    if (!file) return
    setPhotoError('')
    setPhotoBusy(true)
    try {
      const dataUrl = await resizeProfilePhoto(file)
      saveProfilePhoto(currentUser.id, dataUrl)
      setPhotoVersion((v) => v + 1)
    } catch (error) {
      setPhotoError(error instanceof Error ? error.message : 'Could not upload that photo.')
    } finally {
      setPhotoBusy(false)
      if (photoInputRef.current) photoInputRef.current.value = ''
    }
  }

  const removePhoto = () => {
    removeProfilePhoto(currentUser.id)
    setPhotoVersion((v) => v + 1)
    setPhotoError('')
  }

  return (
    <div className="min-h-screen bg-white pb-28 text-slate-900">
      <TopBar title="My Profile" subtitle="Your profile photo and personal details" />
      <div className="flex flex-col gap-5 px-4 py-4">
        <section className="rounded-[24px] border border-[#cfdde9] bg-white p-4 shadow-[0_8px_24px_rgba(55,83,110,.055)]">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <div className="rounded-full border-[3px] border-[#6f93b8] p-[2px]"><Avatar key={photoVersion} user={currentUser} size={68} /></div>
              <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoBusy} aria-label="Change profile photo" className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-[#6f93b8] text-white shadow disabled:opacity-60"><CameraIcon /></button>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => void choosePhoto(e.target.files?.[0])} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black uppercase tracking-[.12em] text-[#7897b5]">My profile</p>
              <p className="mt-1 truncate text-[19px] font-black text-[#2d3d4d]">{currentUser.name}</p>
              <p className="mt-0.5 text-[13px] font-medium text-[#7c8996]">{currentUser.title ?? roleLabels[currentUser.role]}</p>
              <div className="mt-2 flex flex-wrap gap-3">
                <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoBusy} className="text-xs font-black text-[#6f93b8]">{photoBusy ? 'Uploading…' : hasPhoto ? 'Change photo' : 'Add photo'}</button>
                {hasPhoto && <button type="button" onClick={removePhoto} className="text-xs font-black text-slate-400">Remove photo</button>}
              </div>
              {photoError && <p className="mt-2 text-xs font-semibold text-red-600">{photoError}</p>}
            </div>
          </div>
        </section>

        {canSetAvailability && <>
          <section>
            <h3 className="mb-1 text-sm font-bold text-slate-900">My availability</h3>
            <p className="mb-3 text-xs text-slate-500">Pick the days you're generally available. You control this — nobody else can change it, and it never books you in.</p>
            <div className="flex flex-wrap gap-2">{WEEK_DAYS.map((day) => <button key={day} onClick={() => toggleDay(day)} aria-pressed={selectedDays.includes(day)} className={`rounded-xl border px-3.5 py-2.5 text-xs font-semibold ${selectedDays.includes(day) ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500'}`}>{shortLabel[day]}</button>)}</div>
            <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-card">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">You're currently available</p>
              <p className="mt-1 text-sm font-semibold text-slate-800">{chosen.length === 0 ? 'No days selected yet.' : chosen.length === 7 ? 'Every day' : chosen.map((d) => DAY_LABELS[d]).join(', ')}</p>
              <p className="mt-3 text-xs font-bold uppercase tracking-wide text-slate-500">Next 7 days</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">{upcoming.map((date) => { const free = showOnCalendar && selectedDays.includes(dayOfWeekFor(date)); return <span key={date} className={`flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold ${free ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'}`}><span className={`h-2 w-2 rounded-full ${free ? 'bg-emerald-500' : 'bg-slate-300'}`} />{shiftDateLabel(date)}</span> })}</div>
            </div>
            <label className="mt-4 flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-3.5 shadow-card">
              <div className="pr-3"><p className="text-sm font-semibold text-slate-800">Show my availability on the team calendar</p><p className="text-xs text-slate-400">Puts a green dot on your available days inside this participant's Marnixia space.</p></div>
              <button onClick={() => setShowOnCalendar((v) => !v)} aria-pressed={showOnCalendar} className={`relative h-6 w-11 shrink-0 rounded-full transition ${showOnCalendar ? 'bg-emerald-500' : 'bg-slate-200'}`}><span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${showOnCalendar ? 'left-5' : 'left-0.5'}`} /></button>
            </label>
            <button onClick={save} className="mt-4 w-full rounded-2xl bg-[#6f93b8] py-3 text-sm font-bold text-white shadow-card">{justSaved ? 'Saved' : dirty ? 'Save changes' : 'Save availability'}</button>
          </section>
          <WorkerCredentials userId={currentUser.id} />
        </>}
      </div>
    </div>
  )
}

function WorkerCredentials({ userId }: { userId: string }) {
  const [version, setVersion] = useState(0)
  const [editing, setEditing] = useState<CredentialType | null>(null)
  const [fileName, setFileName] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [privacyChecked, setPrivacyChecked] = useState(false)
  const records = useMemo(() => credentialsForWorker(userId), [userId, version])

  const submit = (type: CredentialType, label: string) => {
    if (!privacyChecked || (!fileName.trim() && !referenceNumber.trim())) return
    const existing = records.find((item) => item.type === type)
    saveCredential({ id: existing?.id ?? `cred-${userId}-${type}`, userId, type, label, fileName: fileName.trim() || existing?.fileName, referenceNumber: referenceNumber.trim() || existing?.referenceNumber, expiryDate: expiryDate || existing?.expiryDate, uploadedAt: new Date().toISOString(), privacyChecked: true, reviewedAt: undefined, reviewedByUserId: undefined })
    setEditing(null); setFileName(''); setReferenceNumber(''); setExpiryDate(''); setPrivacyChecked(false); setVersion((v) => v + 1)
  }

  return <section>
    <h3 className="mb-1 text-sm font-bold text-slate-900">Credentials & Documents</h3>
    <p className="mb-3 text-xs text-slate-500">Upload and maintain the credentials you choose to provide to this participant's support team.</p>
    <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-800"><strong>Privacy check:</strong> before uploading, cover or redact your home address and any other personal information that is not needed for the participant or authorised reviewer.</div>
    <div className="flex flex-col gap-2">{CREDENTIAL_TYPES.map((meta) => { const record = records.find((item) => item.type === meta.type); const status = record ? credentialStatus(record) : 'Missing'; return <div key={meta.type} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
      <div className="flex items-center justify-between gap-3"><div><p className="text-sm font-semibold text-slate-900">{meta.label}</p><p className="text-xs text-slate-500">{status}{record?.reviewedAt ? ' · Reviewed' : record ? ' · Not reviewed' : ''}</p></div><button onClick={() => setEditing(editing === meta.type ? null : meta.type)} className="rounded-xl bg-[#eef4f8] px-3 py-2 text-xs font-semibold text-[#607f9e]">{record ? 'Update' : 'Add'}</button></div>
      {record?.fileName && <p className="mt-2 text-xs text-slate-500">{record.fileName}</p>}{record?.referenceNumber && <p className="mt-1 text-xs text-slate-500">Reference: {record.referenceNumber}</p>}{record?.expiryDate && <p className="mt-1 text-xs text-slate-500">Expiry: {new Date(`${record.expiryDate}T00:00:00`).toLocaleDateString()}</p>}
      {editing === meta.type && <div className="mt-3 border-t border-slate-100 pt-3">{!meta.numberOnly && <input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="Document file name" className="input" />}{(meta.numberOnly || meta.type === 'wwcc' || meta.type === 'drivers_licence') && <input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="Number / reference" className="input mt-2" />}{meta.expiry && <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="input mt-2" />}<label className="mt-3 flex items-start gap-2 text-xs text-slate-600"><input type="checkbox" checked={privacyChecked} onChange={(e) => setPrivacyChecked(e.target.checked)} className="mt-0.5" />I have checked this document and removed or covered unnecessary personal address information.</label><button onClick={() => submit(meta.type, meta.label)} disabled={!privacyChecked} className="mt-3 w-full rounded-xl bg-[#6f93b8] py-2.5 text-xs font-bold text-white disabled:opacity-40">Save credential</button></div>}
    </div> })}</div>
  </section>
}

